import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import apiClient from "../utils/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, LogIn, UserPlus } from "lucide-react";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) return; // show login prompt
    if (!token) {
      setStatus("error");
      setMessage("No invitation token found. Please check your invite link.");
      return;
    }
    acceptInvite();
  }, [user, authLoading, token]);

  const acceptInvite = async () => {
    setStatus("loading");
    try {
      const res = await apiClient.post("/orgs/accept-invite", { token }, { skipErrorToast: true });
      setStatus("success");
      setOrgName(res.data.organization?.name || "the organization");
      toast.success("Invitation accepted!");
      setTimeout(() => navigate("/org"), 2000);
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === "string" ? detail : "Failed to accept invitation. The link may be expired or invalid.");
    }
  };

  // Not logged in
  if (!authLoading && !user) {
    const returnPath = `/accept-invite?token=${encodeURIComponent(token || "")}`;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center" data-testid="invite-login-prompt">
          <LogIn className="w-12 h-12 mx-auto text-primary mb-4" />
          <h1 className="font-heading text-xl font-semibold mb-2">
            Sign in to accept your invite
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            You need to be logged in to join an organization. Sign in or create
            an account to continue.
          </p>
          <div className="flex flex-col gap-3">
            <Button asChild className="w-full btn-lift" data-testid="invite-login-btn">
              <Link to={`/login`} state={{ from: { pathname: "/accept-invite", search: `?token=${token}` } }}>
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full" data-testid="invite-register-btn">
              <Link to={`/register`} state={{ from: { pathname: "/accept-invite", search: `?token=${token}` } }}>
                <UserPlus className="w-4 h-4 mr-2" />
                Create Account
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-sm w-full text-center" data-testid="accept-invite-page">
        {(authLoading || status === "loading" || status === "idle") && (
          <div data-testid="invite-loading">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
            <h1 className="font-heading text-xl font-semibold mb-2">
              Accepting Invitation...
            </h1>
            <p className="text-sm text-muted-foreground">Please wait</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="invite-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-4" />
            <h1 className="font-heading text-xl font-semibold mb-2">
              You've joined {orgName}!
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              Redirecting to your organization dashboard...
            </p>
            <Button onClick={() => navigate("/org")} className="btn-lift" data-testid="go-to-org-btn">
              Go to Organization
            </Button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="invite-error">
            <XCircle className="w-14 h-14 mx-auto text-red-400 mb-4" />
            <h1 className="font-heading text-xl font-semibold mb-2">
              Invitation Failed
            </h1>
            <p className="text-sm text-muted-foreground mb-6">{message}</p>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
